import { useEffect, useRef, useState } from 'react';
import type { CompanionMood } from '../../lib/companion';
import { companionPalette } from '../../lib/avatarArt';
import type { CompanionScene } from '../../office/companionScene';
import { cx } from '../ui';

/**
 * The companion, drawn in 3D. three.js is loaded the first time one of these
 * mounts. Where WebGL is unavailable (old hardware, a locked-down browser) it
 * falls back to a shaded SVG of the same character, so nobody gets a hole.
 */

function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
}

export function CompanionCharacter({
  hue,
  mood,
  className,
}: {
  hue: number;
  mood: CompanionMood;
  className?: string;
}) {
  const hostRef = useRef<HTMLDivElement>(null);
  const sceneRef = useRef<CompanionScene | null>(null);
  const initial = useRef({ hue, mood });
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    let cancelled = false;
    void import('../../office/companionScene')
      .then(({ createCompanionScene }) => {
        if (cancelled) return;
        try {
          sceneRef.current = createCompanionScene(host, {
            hue: initial.current.hue,
            mood: initial.current.mood,
            reducedMotion: prefersReducedMotion(),
          });
        } catch {
          setFailed(true);
        }
      })
      .catch(() => setFailed(true));
    return () => {
      cancelled = true;
      sceneRef.current?.destroy();
      sceneRef.current = null;
    };
  }, []);

  useEffect(() => {
    initial.current.hue = hue;
    sceneRef.current?.setHue(hue);
  }, [hue]);

  useEffect(() => {
    initial.current.mood = mood;
    sceneRef.current?.setMood(mood);
  }, [mood]);

  return (
    // Sized by the caller (`className`), never by its own content: the canvas
    // fills whatever box it is given.
    <div ref={hostRef} className={cx('overflow-hidden', className ?? 'relative h-24 w-24')} aria-hidden>
      {failed ? <CompanionFallback hue={hue} /> : null}
    </div>
  );
}

/**
 * The 3D character as a still picture, for the many small places it appears
 * (chat bubbles, comment pins, avatars) where a live 3D view each would be too
 * much for the browser. Rendered once per colour, offscreen, and cached.
 */
const portraits = new Map<number, Promise<string>>();

function renderPortrait(hue: number): Promise<string> {
  let pending = portraits.get(hue);
  if (!pending) {
    pending = import('../../office/companionScene').then(({ createCompanionScene }) => {
      const host = document.createElement('div');
      host.style.cssText = 'position:fixed;left:-10000px;top:0;width:192px;height:192px;pointer-events:none;';
      document.body.appendChild(host);
      try {
        // Still mode renders one frame straight away, so the picture can be read at once.
        const scene = createCompanionScene(host, { hue, mood: 'idle', reducedMotion: true });
        const canvas = host.querySelector('canvas');
        const url = canvas ? canvas.toDataURL('image/png') : '';
        scene.destroy();
        if (!url || url.length < 2000) throw new Error('empty render');
        return url;
      } finally {
        host.remove();
      }
    });
    pending.catch(() => portraits.delete(hue));
    portraits.set(hue, pending);
  }
  return pending;
}

export function useCompanionPortrait(hue: number): string | null {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    renderPortrait(hue)
      .then((value) => !cancelled && setUrl(value))
      .catch(() => !cancelled && setUrl(null));
    return () => {
      cancelled = true;
    };
  }, [hue]);
  return url;
}

/** The same creature as a flat-shaded SVG. */
export function CompanionFallback({ hue }: { hue: number }) {
  const palette = companionPalette(hue);
  const id = `cf${hue}`;
  return (
    <svg viewBox="0 0 100 100" className="h-full w-full">
      <defs>
        <radialGradient id={`${id}b`} cx="36%" cy="30%" r="80%">
          <stop offset="0%" stopColor={palette.light} />
          <stop offset="55%" stopColor={palette.base} />
          <stop offset="100%" stopColor={palette.dark} />
        </radialGradient>
      </defs>
      <ellipse cx="50" cy="92" rx="26" ry="4" fill="#1e293b" opacity="0.14" />
      <line x1="50" y1="22" x2="50" y2="10" stroke={palette.dark} strokeWidth="2.5" strokeLinecap="round" />
      <circle cx="50" cy="9" r="4.5" fill={palette.belly} />
      <ellipse cx="38" cy="86" rx="9" ry="5" fill={palette.dark} />
      <ellipse cx="62" cy="86" rx="9" ry="5" fill={palette.dark} />
      <ellipse cx="50" cy="56" rx="32" ry="30" fill={`url(#${id}b)`} />
      <ellipse cx="50" cy="68" rx="18" ry="13" fill={palette.belly} opacity="0.9" />
      <ellipse cx="40" cy="52" rx="4.5" ry="5.5" fill="#1e1b3a" />
      <ellipse cx="60" cy="52" rx="4.5" ry="5.5" fill="#1e1b3a" />
      <circle cx="38.5" cy="50" r="1.6" fill="#fff" />
      <circle cx="58.5" cy="50" r="1.6" fill="#fff" />
      <circle cx="31" cy="60" r="4" fill="#ff8fb8" opacity="0.55" />
      <circle cx="69" cy="60" r="4" fill="#ff8fb8" opacity="0.55" />
      <path d="M46 60 Q50 64 54 60" stroke="#6b1d3a" strokeWidth="1.8" fill="none" strokeLinecap="round" />
    </svg>
  );
}
